import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardMedia,
  Button,
  IconButton,
  Chip,
  Divider,
  TextField,
  CircularProgress,
  Alert,
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import {
  ArrowBack,
  AddShoppingCart,
  Scale,
  LocalOffer,
  CheckCircle,
  Add,
  Remove,
  Inventory,
} from '@mui/icons-material';
import { getProduct } from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Product } from '../types';
import { colors } from '../utils/constants';
import toast from 'react-hot-toast';
import { formatMoney, getProductUnitPrice, getCasePrice, formatCasePriceBreakdown } from '../utils/pricing';
import { getDefaultCaseQuantity, getTotalItemCount, calculateCustomCasePrice } from '../utils/caseQuantity';
import CaseQuantitySelector from '../components/CaseQuantitySelector';
import FavoriteButton from '../components/FavoriteButton';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCaseQuantity, setSelectedCaseQuantity] = useState<number>(1);
  const [numberOfCases, setNumberOfCases] = useState<number>(1);
  const [casesInputValue, setCasesInputValue] = useState<string>('1');
  const [isCustomCase, setIsCustomCase] = useState<boolean>(false);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      
      try {
        const productData = await getProduct(parseInt(id));
        setProduct(productData);
        setSelectedCaseQuantity(getDefaultCaseQuantity(productData));
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (!product) return;
    
    addToCart(product.id, selectedCaseQuantity, numberOfCases, true, isCustomCase);
    const totalItems = getTotalItemCount(selectedCaseQuantity, numberOfCases);
    // toast.success(`Added ${totalItems} of ${product.name} to cart`);
  };

  const handleCaseQuantityChange = (newCaseQuantity: number, isCustom?: boolean) => {
    setSelectedCaseQuantity(newCaseQuantity);
    if (isCustom !== undefined) {
      setIsCustomCase(isCustom);
    }
  };
  
  const handleCustomCaseToggle = (isCustom: boolean) => {
    setIsCustomCase(isCustom);
  };

  const handleNumberOfCasesChange = (newNumberOfCases: number) => {
    if (newNumberOfCases < 1) {
      setNumberOfCases(1);
      setCasesInputValue('1');
      return;
    }
    setNumberOfCases(newNumberOfCases);
    setCasesInputValue(newNumberOfCases.toString());
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} sx={{ color: colors.primary }} />
        </Box>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" gutterBottom>
            Product not found
          </Typography>
          <Button onClick={() => navigate('/products')} variant="outlined">
            Back to Products
          </Button>
        </Box>
      </Container>
    );
  }

  const productDeposit = product?.lost_amount ?? 0;
  const productService = product?.service_price ?? 0;
  const productUnitPrice = getProductUnitPrice(product);
  const selectedCasePrice = isCustomCase 
    ? calculateCustomCasePrice(product, selectedCaseQuantity)
    : getCasePrice(product, selectedCaseQuantity);
  const totalPrice = selectedCasePrice * numberOfCases;

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/products')}
          sx={{ mb: 4 }}
        >
          Back to Products
        </Button>

        <Card variant="outlined" sx={{ borderRadius: 3, borderColor: 'grey.300' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 4 } }}>
            {/* Product Image */}
            <Box sx={{ p: 3, bgcolor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CardMedia
                component="img"
                height="500"
                image={product.image_url || '/placeholder-product.png'}
                alt={product.name}
                sx={{
                  objectFit: 'contain',
                  maxHeight: '100%',
                  maxWidth: '100%',
                }}
                onError={(e) => {
                  e.currentTarget.src = '/placeholder-product.png';
                }}
              />
            </Box>

            {/* Product Details */}
            <Box sx={{ p: { xs: 2, md: 4 } }}>
              {/* Product Name and Favorite Button */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, flexGrow: 1 }}>
                  {product.name}
                </Typography>
                <FavoriteButton product={product} size="large" />
              </Box>

              {/* Description */}
              {/* <Typography variant="h6" color="text.secondary" gutterBottom>
                {product.description || 'No description available'}
              </Typography> */}

              {/* Price Breakdown */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Price Breakdown
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      {/* For single items, show simplified pricing */}
                      {selectedCaseQuantity === 1 ? (
                        <TableRow sx={{ fontWeight: 'bold' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Price per item:</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatMoney(selectedCasePrice)}</TableCell>
                        </TableRow>
                      ) : (
                        /* For multi-quantity cases, show detailed breakdown */
                        <>
                          <TableRow>
                            <TableCell>Unit Service Fee:</TableCell>
                            <TableCell align="right">{formatMoney(productUnitPrice)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              {selectedCaseQuantity} × Unit Price
                            </TableCell>
                            <TableCell align="right">{formatMoney(productUnitPrice * selectedCaseQuantity)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Packed case:</TableCell>
                            <TableCell align="right">{formatMoney(product.pricing_info?.case_packing_cost_cents ?? 0)}</TableCell>
                          </TableRow>
                          <TableRow sx={{ fontWeight: 'bold' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Service Fee per case of {selectedCaseQuantity}:</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatMoney(selectedCasePrice)}</TableCell>
                          </TableRow>
                        </>
                      )}
                      {/* <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Total Price for {getTotalItemCount(selectedCaseQuantity, numberOfCases)} items:</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatMoney(totalPrice)}</TableCell>
                      </TableRow> */}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Product Tags */}
              {/* <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {product.is_lid && <Chip label="Lid" color="primary" variant="outlined" />}
                {product.is_case && <Chip label="Case" color="primary" variant="outlined" />}
                {product.is_case_lid && <Chip label="Case Lid" color="primary" variant="outlined" />}
                {product.needs_lid && <Chip label="Needs Lid" color="warning" variant="outlined" />}
              </Box> */}

              {/* Product Details - commented out for now, till we have more accurate info */}
              {/* <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Product Details
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 2, '& > *': { py: 1 } }}>
                  {product.manufacturer && (
                    <>
                      <Typography variant="body2" color="text.secondary">Manufacturer:</Typography>
                      <Typography variant="body2">{product.manufacturer}</Typography>
                    </>
                  )}
                  {product.material && (
                    <>
                      <Typography variant="body2" color="text.secondary">Material:</Typography>
                      <Typography variant="body2">{product.material}</Typography>
                    </>
                  )}
                  {product.color && (
                    <>
                      <Typography variant="body2" color="text.secondary">Color:</Typography>
                      <Typography variant="body2">{product.color}</Typography>
                    </>
                  )}
                  {product.weight_grams && (
                    <>
                      <Typography variant="body2" color="text.secondary">Weight:</Typography>
                      <Typography variant="body2">{product.weight_grams}g</Typography>
                    </>
                  )}
                  {product.flush_volume_oz && (
                    <>
                      <Typography variant="body2" color="text.secondary">Flush Volume:</Typography>
                      <Typography variant="body2">{product.flush_volume_oz} oz</Typography>
                    </>
                  )}
                  {product.sku && (
                    <>
                      <Typography variant="body2" color="text.secondary">SKU:</Typography>
                      <Typography variant="body2">{product.sku}</Typography>
                    </>
                  )}
                </Box>
              </Box> */}

              <Divider sx={{ my: 3 }} />

              {/* Case Quantity Selector */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Case Size
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <CaseQuantitySelector
                    product={product}
                    selectedCaseQuantity={selectedCaseQuantity}
                    onCaseQuantityChange={handleCaseQuantityChange}
                    size="medium"
                    isCustomCase={isCustomCase}
                    onCustomCaseToggle={handleCustomCaseToggle}
                  />
                </Box>
                
                {/* Number of Cases */}
                <Typography variant="h6" gutterBottom>
                  Number of Cases
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <IconButton 
                    onClick={() => handleNumberOfCasesChange(numberOfCases - 1)}
                    disabled={numberOfCases <= 1}
                  >
                    <Remove />
                  </IconButton>
                  <TextField
                    value={casesInputValue}
                    onChange={(e) => {
                      setCasesInputValue(e.target.value);
                      const n = parseInt(e.target.value, 10);
                      if (!isNaN(n) && n >= 1) {
                        setNumberOfCases(n);
                      }
                    }}
                    onBlur={() => setCasesInputValue(numberOfCases.toString())}
                    inputProps={{ min: 1, style: { textAlign: 'center' } }}
                    sx={{ width: 80 }}
                    size="small"
                  />
                  <IconButton 
                    onClick={() => handleNumberOfCasesChange(numberOfCases + 1)}
                  >
                    <Add />
                  </IconButton>
                </Box>
                
                {/* Total Items Display */}
                <Typography variant="body2" color="text.secondary">
                  Total: {getTotalItemCount(selectedCaseQuantity, numberOfCases)} items
                </Typography>
              </Box>

              {/* Add to Cart */}
              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<AddShoppingCart />}
                onClick={handleAddToCart}
                sx={{
                  py: 1.5,
                  mb: 2,
                  fontSize: '1.1rem',
                  borderColor: colors.primary,
                  color: colors.primary,
                  '&:hover': {
                    backgroundColor: colors.primary,
                    color: '#fff',
                    borderColor: colors.primary,
                  }
                }}
              >
                Add {getTotalItemCount(selectedCaseQuantity, numberOfCases)} to Cart ({formatMoney(totalPrice)})
              </Button>
              {product?.lost_amount > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  {user?.default_invoice_type === 'one-time' ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.6 }}>
                      <strong>Event-based invoicing:</strong><br />
                      A lost fee of {formatMoney(product.lost_amount)} will be charged per item if not returned.
                    </Typography>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.6, mb: 2 }}>
                        <strong>Monthly invoicing for closed venues:</strong><br />
                        A deposit of {formatMoney(product.lost_amount + product.service_price)} is applied to each item delivered and automatically refunded back as they are returned.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.6 }}>
                        <strong>Monthly invoicing for open environments:</strong><br />
                        A deposit of {formatMoney(product.lost_amount + product.service_price)} is applied to each item delivered, for you to pass on to your customers, who are refunded back after they scan and return.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Card>
      </Box>
    </Container>
  );
};

export default ProductDetailPage; 